import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import {
  getDiagnosticQuestions,
  submitDiagnostic,
  trackEvent,
} from "@/lib/api";
import { trackGoogleEvent } from "@/lib/googleTracking";
import { STORE } from "@/constants/testIds";

const DISCLAIMER =
  "Illinois Broker Prep is an independent study aid. Not affiliated with PSI, IDFPR, the State of Illinois, or any real estate commission. No actual exam questions. No pass guarantee.";

const Container = ({ children }) => (
  <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">{children}</div>
);

const LETTERS = ["A", "B", "C", "D"];

const portionLabel = (p) =>
  ({ state: "Illinois State", national: "National", math: "Math" }[p] || p);

export default function Diagnostic() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const { data: questions, isLoading, isError } = useQuery({
    queryKey: ["diagnostic-questions"],
    queryFn: getDiagnosticQuestions,
  });

  useEffect(() => {
    trackEvent("diagnostic_start");
    trackGoogleEvent("diagnostic_start");
  }, []);

  const total = questions?.length || 0;
  const current = questions?.[index];
  const selected = current ? answers[current.id] : undefined;
  const progress = useMemo(
    () => (total ? Math.round(((index + 1) / total) * 100) : 0),
    [index, total]
  );

  const choose = (letter) => {
    if (!current) return;
    setAnswers((prev) => ({ ...prev, [current.id]: letter }));
    trackEvent("diagnostic_question_answered", {
      question_id: current.id,
      portion: current.portion,
    });
  };

  const goNext = async () => {
    if (index < total - 1) {
      setIndex((i) => i + 1);
      return;
    }
    // last question -> submit
    setSubmitting(true);
    try {
      const result = await submitDiagnostic(answers);
      trackEvent("diagnostic_complete", {
        correct: result.correct,
        total: result.total,
      });
      trackGoogleEvent("diagnostic_complete", {
        correct: result.correct,
        total: result.total,
      });
      try {
        sessionStorage.setItem("ibp_diag_result", JSON.stringify(result));
      } catch (e) {
        // sessionStorage may be unavailable (private mode); navigation still carries the result
        if (process.env.NODE_ENV !== "production") {
          console.debug("Could not cache diagnostic result:", e && e.message);
        }
      }
      navigate("/diagnostic/results", { state: { result } });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={STORE.diagnosticPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-14">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Free diagnostic"
            title="Find your weak areas in 10 questions"
            subtitle="No login required. Answer 10 questions across Illinois state law, national concepts, and math. See your score and where to focus."
          />
        </Container>
      </section>

      <section className="py-12 sm:py-16">
        <Container>
          {isLoading && (
            <div className="py-10 text-center text-sm text-[color:rgba(11,18,32,0.6)]">
              Loading questions...
            </div>
          )}
          {isError && (
            <div className="rounded-xl border border-[color:rgba(155,44,44,0.3)] bg-[color:rgba(155,44,44,0.06)] px-4 py-3 text-sm text-[#9b2c2c]">
              Could not load the diagnostic. Please refresh and try again.
            </div>
          )}

          {current && (
            <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-8">
              <div className="flex items-center justify-between">
                <p
                  data-testid={STORE.diagnosticProgress}
                  className="text-sm font-semibold text-[var(--ibp-ink)]"
                >
                  Question {index + 1} of {total}
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full bg-[var(--ibp-navy-950)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                    {portionLabel(current.portion)}
                  </span>
                </div>
              </div>

              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[color:rgba(10,27,51,0.1)]">
                <div
                  className="h-full rounded-full bg-[var(--ibp-gold-500)] transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <h2 className="mt-6 text-lg font-semibold leading-7 text-[var(--ibp-ink)]">
                {current.question_text}
              </h2>

              <div className="mt-5 space-y-3">
                {LETTERS.map((letter) => {
                  const isSel = selected === letter;
                  return (
                    <button
                      key={letter}
                      type="button"
                      data-testid={`${STORE.diagnosticChoice}-${letter}`}
                      onClick={() => choose(letter)}
                      className={[
                        "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                        isSel
                          ? "border-[var(--ibp-gold-500)] bg-[color:rgba(214,162,30,0.10)]"
                          : "border-[color:rgba(10,27,51,0.15)] bg-white hover:border-[var(--ibp-navy-950)]",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-lg text-sm font-bold",
                          isSel
                            ? "bg-[var(--ibp-navy-950)] text-[var(--ibp-gold-500)]"
                            : "bg-[var(--ibp-offwhite)] text-[var(--ibp-ink)]",
                        ].join(" ")}
                      >
                        {letter}
                      </span>
                      <span className="text-sm leading-6 text-[var(--ibp-ink)]">
                        {current.choices[letter]}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-7 flex items-center justify-between">
                <button
                  type="button"
                  data-testid={STORE.diagnosticBack}
                  onClick={() => setIndex((i) => Math.max(0, i - 1))}
                  disabled={index === 0}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:bg-[color:rgba(10,27,51,0.06)] disabled:opacity-40"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button
                  type="button"
                  data-testid={STORE.diagnosticNext}
                  onClick={goNext}
                  disabled={!selected || submitting}
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.2)] transition-colors hover:bg-[var(--ibp-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ibp-focus-ring)] focus-visible:ring-offset-2 disabled:opacity-60"
                >
                  {index < total - 1 ? (
                    <>
                      Next <ArrowRight className="h-4 w-4" />
                    </>
                  ) : submitting ? (
                    "Scoring..."
                  ) : (
                    "See my results"
                  )}
                </button>
              </div>
            </div>
          )}

          <p className="mx-auto mt-8 max-w-2xl text-center text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
            {DISCLAIMER}
          </p>
        </Container>
      </section>
    </div>
  );
}
